// Phase I0.3 — TeamLine primitive.
// badge + name row with the **kit-colour-monogram fallback** required by
// CLAUDE.md hard rule: when an external badge URL fails to load, the
// `onError` handler swaps to a 3-letter monogram on the team's brand
// kit colour. Used by DailyFixtureRail and any team-row surface.

import { useState, type CSSProperties } from "react";

export interface TeamLineProps {
  shortCode: string;
  name: string;
  badgeUrl?: string | null;
  /** Brand kit colour for the monogram fallback. */
  kitColor: string;
  /** Optional score to render at the right edge. */
  score?: string | number | null;
  /** Visually de-emphasise (e.g. losing side of a settled fixture). */
  dim?: boolean;
  /** Badge size in px (also used as monogram tile size). Default 24. */
  size?: number;
  style?: CSSProperties;
}

export function TeamLine({ shortCode, name, badgeUrl, kitColor, score, dim, size = 24, style }: TeamLineProps) {
  const [imgFailed, setImgFailed] = useState(false);
  const showImg = Boolean(badgeUrl) && !imgFailed;
  const monogram = shortCode.slice(0, 3).toUpperCase();
  const monogramFontSize = Math.max(8, Math.round(size * 0.42));
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        opacity: dim ? 0.55 : 1,
        ...style,
      }}
    >
      {showImg ? (
        <img
          src={badgeUrl ?? undefined}
          alt={shortCode}
          onError={() => setImgFailed(true)}
          style={{
            width: size,
            height: size,
            objectFit: "contain",
            flexShrink: 0,
            filter: "drop-shadow(0 1px 2px rgba(0,0,0,0.4))",
          }}
        />
      ) : (
        <span
          aria-hidden
          style={{
            width: size,
            height: size,
            borderRadius: 3,
            background: kitColor,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            fontFamily: "var(--sa-font-stat)",
            fontSize: monogramFontSize,
            color: "#fff",
            letterSpacing: "0.04em",
            flexShrink: 0,
          }}
        >
          {monogram}
        </span>
      )}
      <span
        style={{
          fontFamily: "var(--sa-font-body)",
          fontSize: 13.5,
          color: "var(--sa-fg-primary)",
          flex: 1,
          fontWeight: 500,
        }}
      >
        {name}
      </span>
      {score !== null && score !== undefined && score !== "" ? (
        <span
          style={{
            fontFamily: "var(--sa-font-stat)",
            fontSize: 22,
            color: "var(--sa-fg-primary)",
            lineHeight: 1,
          }}
        >
          {score}
        </span>
      ) : null}
    </div>
  );
}
